import {moment} from 'moment';
import {_} from 'lodash';
import {
    ICompanySyncstep,
    IEHubProcess,
    IImportHrFile,
    IMessageData,
    IObjectData,
    IRouteDefinition,
    IRouteParams,
    ISearchAuditLogParams,
    ISearchGroupMemberParams,
    ISearchMessageLogParams,
    ISearchObjectParams,
    ISearchParams,
    ISearchSystemParams,
    ISystem,
    TSyncfield
} from "../index";
import {storeObservable} from "./sessionstore";
import {global} from "../appContext";
import {DEFAULT_SEARCH_COLUMNS, ObjectTypeId, SPINNER} from "../constants";
import {getServiceDefinition, TServiceName, TServiceParameter} from "../constants/ApiServices";
import {_d, getObjectDisplayName, getObjectDisplayNameFn} from "./misc";
import {nsSyncFields} from "../req/nsSyncFields";
import {convertToServerString, getDateTime} from "../util/datetime";

export type TSearchParams = ISearchParams | ISearchSystemParams | ISearchObjectParams | ISearchAuditLogParams

export function getSearchParams(...parameters: Partial<ISearchParams>[]): ISearchParams {
    const params: ISearchParams = _.defaultsDeep.apply(null, parameters)
    return _.defaults(params, {
        asc: true,
        orgnr: global.login.selectedOrganizationId(),
        pagenr: 0,
        resultsprpage: storeObservable("num.search.results")(),
        searchkey: "",
        sortcol: "",
        token: global.login.getToken(),
    });
}

export function getSearchSystemParams(...parameters: Partial<ISearchSystemParams>[]): ISearchSystemParams {
    const params: ISearchSystemParams = _.defaultsDeep(null, ...parameters)
    const defaultParams: ISearchParams = getSearchParams()
    return _.defaults(params, defaultParams, {targetid: global.login.user()?.id});
}

export function getSearchAuditLogParams(...parameters: Partial<ISearchAuditLogParams>[]): ISearchAuditLogParams {
    const params: ISearchAuditLogParams = _.defaultsDeep(null, ...parameters)
    const defaultParams: Partial<ISearchAuditLogParams> = getSearchParams()
    return _.defaults(params, defaultParams, {
        fromdate: '',
        todate: '',
    });
}

export function getSearchObjectParams(...parameters: Partial<ISearchObjectParams>[]): ISearchObjectParams {
    const params: ISearchObjectParams = _.defaultsDeep(null, ...parameters)
    const objectTypeId = params.objecttypeid ?? ObjectTypeId.User;
    const searchColumns = params.searchcols?.split(',') ?? DEFAULT_SEARCH_COLUMNS[objectTypeId];

    if (objectTypeId == ObjectTypeId.User) {
        if (!searchColumns.includes('DisplayName') && searchColumns.includes('GivenName') && searchColumns.includes('Surname')) {
            searchColumns.push('DisplayName')
        }
    }

    const searchColumnsStr = searchColumns.join(',')

    return _.defaults(params, getSearchParams(), {
        asc: true,
        deleted: 1,
        importfileid: 0,
        jruleset: '',
        manualobjects: false,
        objecttypeid: objectTypeId,
        onlyids: false,
        onlytuples: false,
        orgnr: global.login.selectedOrganizationId(),
        pagenr: 0,
        resultsprpage: 1,
        searchcols: searchColumnsStr,
        searchkey: '',
        sortcol: '',
        syncfieldids: '',
        syncstep: 0,
        token: global.login.getToken(),
    });
}

export function getSearchObjectParamsWithSyncfields(objectTypeId: ObjectTypeId, keys: TSyncfield[], ...parameters: Partial<ISearchObjectParams>[]): Promise<ISearchObjectParams> {
    return nsSyncFields.getSyncfieldIdsFromNames(objectTypeId, ...keys)
        .then((data: string) => {
            return getSearchObjectParams({
                searchcols: keys.join(','),
                objecttypeid: objectTypeId,
                syncfieldids: data,
            }, ...parameters)
        })
}

export async function searchObjectWithSyncfields(objectTypeId: ObjectTypeId, keys: TSyncfield[], ...parameters: Partial<ISearchObjectParams>[]): Promise<IObjectData[] | string[][]> {
    return getSearchObjectParamsWithSyncfields(objectTypeId, keys, ...parameters)
        .then((data: ISearchObjectParams) => global.getPromiseRequest('searchobject').request(data))
}

export function getSearchGroupMemberParams(params: Partial<ISearchGroupMemberParams>): ISearchGroupMemberParams {
    return _.defaultsDeep(params, {objectid: global.getRouteId(), searchcols: 'id'}, getSearchParams(params));
}

export function getSearchObjectHistory(params: Partial<ISearchGroupMemberParams>): ISearchParams {
    return _.extend(getSearchParams(params), {objectid: global.getRouteId(), sortcol: 'Id'}, params);
}

export function getSearchMessageLogParams(params: Partial<ISearchMessageLogParams>): ISearchMessageLogParams {
    const fromdate = moment({
        year: 2000,
        month: 1,
        day: 1,
    })
    const todate = moment().endOf('day')
    return _.defaultsDeep(getSearchParams(params), {
        fromdate: convertToServerString(fromdate),
        messageid: global.getRouteId() ?? 0,
        todate: convertToServerString(todate),
    });
}

export function getSearchParamsFn(servicename: TServiceName): ((...parameters: Partial<TSearchParams>[]) => TSearchParams) {
    switch (servicename) {
        case 'searchobject':
            return getSearchObjectParams
        case 'searchsystem':
            return getSearchSystemParams
        case 'searchauditlog':
        case 'searchauditlogsecurity':
        case 'searchauditlogerror':
            return getSearchAuditLogParams
        case 'searchobjecthistory':
            return getSearchObjectHistory
        case 'searchmessagelog':
            return getSearchMessageLogParams
        default:
            return getSearchParams
    }
}

export function getParams(serviceName: TServiceName, params: ISearchParams | ISearchObjectParams) {
    const serviceDefinition = getServiceDefinition(serviceName);
    const serviceParams = serviceDefinition.paramKeys;

    const p: any = _.cloneDeep(params ?? {});

    if (serviceParams.includes('token')) {
        p.token = global.login.getToken();
    }

    if (!p.orgnr && serviceParams.includes('orgnr')) {
        p.orgnr = global.login.selectedOrganizationId();
    }

    if ('searchkey' in p) {
        p.searchkey = p.searchkey.replace(/(\[)|(])/gm, (match: string, open?: string, close?: string) => {
            if (open) {
                return '[[]';
            }
            if (close) {
                // no need to escape
                return ']';
            }
        });
    }

    validateParams(serviceName, p);

    return p;
}

interface IParameterObject {
    [key: string]: any
}

export function validateParams(serviceName: TServiceName, params: IParameterObject): boolean {
    const used = Object.keys(params).sort();
    const expected = _.sortBy(getServiceDefinition(serviceName).paramKeys, (s: TServiceParameter) => s);
    const equal = _.isEqual(used, expected);
    const difference: TServiceParameter[] = _.filter([...used, ...expected] as TServiceParameter[], (p: TServiceParameter) => !used.includes(p) || !expected.includes(p))
    if (!equal) {
        _d.warn(`Parameter list is not valid for ${serviceName}.`)
        _d.groupCollapsed('see details');
        _d.info('used', used);
        _d.info('expected', expected);
        _d.info('difference', difference);
        _d.groupEnd();
    }
    return equal;
}

export function xhrFactory(percentComplete: KnockoutObservable<number>, messageData: KnockoutObservable<IMessageData>, message: string = `((loading data|td))`) {
    messageData({message: `${message} ${SPINNER}`});
    return () => {
        const xhr = new XMLHttpRequest();
        xhr.addEventListener("progress", (evt) => {
            if (evt.lengthComputable) {
                percentComplete(evt.loaded / evt.total);
                messageData({message: `${message} (${(percentComplete() * 100).toFixed(1)}%)`})
            }
        }, false);
        return xhr;
    }
}

export function getObjectFromRouteData(data: any, def?: IRouteDefinition, params?: IRouteParams): any {
    const currentRoute = global.router.currentRoute()
    const routeDef = def ?? currentRoute?.__routeDef
    const routeParams = params ?? currentRoute?.__routeParams ?? {}

    if (!data || !routeDef) {
        return null;
    }

    switch (routeDef.fetchRouteDef?.serviceName) {
        case 'getcompanysyncsteps':
            if (Array.isArray(data)) {
                return (data as ICompanySyncstep[]).find(o => o.id == routeParams['id']);
            }
            break;
        case 'searchsystem':
            if (Array.isArray(data)) {
                return (data as ISystem[]).find(o => o.id == routeParams['id']);
            }
            break;
        case 'getorganizationchart':
            if (Array.isArray(data)) {
                return (data as IObjectData[]).find(o => o.id == routeParams['id']);
            }
            break;
        case 'getehubprocess':
            if (Array.isArray(data)) {
                return (data as IEHubProcess[]).find(o => o.id == routeParams['id']);
            }
    }
    if (Array.isArray(data)) {
        return data[0];
    } else {
        return data;
    }
}

export function getImportHrFileStarted(importFile: IImportHrFile): string | null {
    return importFile?.started ?? null;
}

export function getImportHrFileDisplayStarted(importFile: IImportHrFile): string | null {
    const started = getImportHrFileStarted(importFile);
    return started ? getDateTime(started, true) : null;
}

export function getDisplayNameForRouteData(data: any, def: IRouteDefinition, params: IRouteParams) {
    const d = getObjectFromRouteData(data, def, params);
    if (!d) {
        return null;
    }
    switch (def.fetchRouteDef?.serviceName) {
        case 'getcompanysyncsteps':
            return d.syncstep.name;
    }
    switch (def.id) {
        case 'importhistorydetails':
            return getImportHrFileDisplayStarted(d as IImportHrFile) ?? String((d as IImportHrFile).id);
    }
    if ('name' in d) {
        return d.name;
    }
    if ('type' in d) {
        return getObjectDisplayName(d);
    }
    if ('objectTypeId' in def && def.objectTypeId !== undefined) {
        return getObjectDisplayNameFn(def.objectTypeId)(d);
    }

    _d.warn(`No name found for ${def.id}`, {data: d, def, params});
    return 'N/A';
}